import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { IconClock, IconPlus } from "@/components/SvoIcons";
import { ConfirmDialog } from "@/components/ConfirmDialog";

type Action = "set" | "extend" | "pause" | "resume" | "reset";

export function AdminTimerSection() {
  const { state, dispatch } = useStore();
  const [minutes, setMinutes] = useState("15");
  const [busy, setBusy] = useState<Action | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [, setTick] = useState(0);

  // Re-render once per second so the live remaining-time stays accurate.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const phase = state.workshop.phase;
  const endsAt = phase.timerEndsAt;
  const pausedRemainingSec = phase.timerPausedRemainingSec;
  const isPaused = !endsAt && typeof pausedRemainingSec === "number" && pausedRemainingSec > 0;
  const isRunning = !!endsAt;
  const remainingSec = endsAt
    ? Math.max(0, Math.round((endsAt - Date.now()) / 1000))
    : isPaused
      ? pausedRemainingSec!
      : null;

  const apply = async (mode: "set" | "extend") => {
    const n = Number(minutes);
    if (!Number.isFinite(n) || n <= 0) return;
    setBusy(mode);
    setMsg(null);
    try {
      const r = await api.admin.setTimer(state.auth.token, n * 60, mode);
      dispatch({ type: "phase/set", phase: r.phase });
      setMsg(
        mode === "set"
          ? `Timer auf ${n} Minuten gesetzt.`
          : `Timer um ${n} Minuten verlängert.`,
      );
    } catch (err) {
      console.error(err);
      setMsg("Konnte Timer nicht setzen.");
    } finally {
      setBusy(null);
    }
  };

  const pause = async () => {
    setBusy("pause");
    setMsg(null);
    try {
      const r = await api.admin.pauseTimer(state.auth.token);
      dispatch({ type: "phase/set", phase: r.phase });
      setMsg("Timer pausiert.");
    } catch (err) {
      console.error(err);
      setMsg("Konnte nicht pausieren.");
    } finally {
      setBusy(null);
    }
  };

  const resume = async () => {
    setBusy("resume");
    setMsg(null);
    try {
      const r = await api.admin.resumeTimer(state.auth.token);
      dispatch({ type: "phase/set", phase: r.phase });
      setMsg("Timer läuft wieder.");
    } catch (err) {
      console.error(err);
      setMsg("Konnte nicht fortsetzen.");
    } finally {
      setBusy(null);
    }
  };

  const doReset = async () => {
    setConfirmReset(false);
    setBusy("reset");
    setMsg(null);
    try {
      const r = await api.admin.resetTimer(state.auth.token);
      dispatch({ type: "phase/set", phase: r.phase });
      setMsg("Timer zurückgesetzt.");
    } catch (err) {
      console.error(err);
      setMsg("Konnte Timer nicht zurücksetzen.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="admin-grid">
      <header className="admin-section-head">
        <h2>Timer</h2>
        <p>Anforderungs-Timer setzen oder verlängern. Gilt während Phase „Anforderung".</p>
      </header>

      <div className="timer-card">
        <div className="timer-eyebrow">Aktueller Stand</div>
        <div className="timer-phase">
          Phase: <b>{phase.phase}</b>
          {isPaused && (
            <span className="timer-paused-pill" style={{ marginLeft: 12 }}>
              ⏸ pausiert
            </span>
          )}
        </div>
        {remainingSec !== null ? (
          <div className="timer-clock">
            {String(Math.floor(remainingSec / 60)).padStart(2, "0")}:
            {String(remainingSec % 60).padStart(2, "0")}
            <small>{isPaused ? "pausiert" : "verbleibend"}</small>
          </div>
        ) : (
          <div className="timer-empty">Kein Timer aktiv.</div>
        )}
        <div className="admin-toolbar" style={{ marginTop: 12 }}>
          {isRunning && (
            <button
              type="button"
              onClick={pause}
              disabled={busy !== null}
              className="btn btn-ghost"
            >
              {busy === "pause" ? "…" : "⏸ Pause"}
            </button>
          )}
          {isPaused && (
            <button
              type="button"
              onClick={resume}
              disabled={busy !== null}
              className="btn btn-success"
            >
              {busy === "resume" ? "…" : "▶ Fortsetzen"}
            </button>
          )}
          {(isRunning || isPaused) && (
            <button
              type="button"
              onClick={() => setConfirmReset(true)}
              disabled={busy !== null}
              className="btn btn-danger"
            >
              {busy === "reset" ? "…" : "↺ Zurücksetzen"}
            </button>
          )}
        </div>
      </div>

      <div
        className="admin-grid"
        style={{
          padding: "16px 18px",
          border: "1px solid var(--svo-grau-100)",
          borderRadius: "var(--radius-xl)",
          background: "#fff",
          gap: 12,
        }}
      >
        <label className="login-field-label">Dauer (Minuten)</label>
        <div className="admin-toolbar">
          <input
            className="input-text"
            style={{ width: 120, fontVariantNumeric: "tabular-nums" }}
            type="number"
            min="1"
            max="120"
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
          />
          <button
            type="button"
            onClick={() => apply("set")}
            disabled={busy !== null}
            className="btn btn-primary"
          >
            <IconClock size={16} />
            {busy === "set" ? "…" : "Timer setzen (überschreibt)"}
          </button>
          <button
            type="button"
            onClick={() => apply("extend")}
            disabled={busy !== null || endsAt == null}
            className="btn btn-ghost"
          >
            <IconPlus size={16} />
            {busy === "extend" ? "…" : "verlängern"}
          </button>
        </div>
        {msg && (
          <div
            style={{
              color: "var(--svo-gruen)",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {msg}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmReset}
        title="Timer zurücksetzen?"
        message="Der laufende Countdown wird beendet. Auf den Handys verschwindet der Timer sofort."
        confirmLabel="Ja, zurücksetzen"
        cancelLabel="Abbrechen"
        variant="danger"
        onConfirm={doReset}
        onCancel={() => setConfirmReset(false)}
        busy={busy === "reset"}
      />
    </div>
  );
}
